import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Card,
	Typography,
	Spin,
	Button,
	Space,
	Row,
	Col,
	Tag,
} from 'antd'
import {
	PictureOutlined,
	BankOutlined,
	EditOutlined,
	GlobalOutlined,
} from '@ant-design/icons'
import { useBranding } from '../../common/branding/useBranding'
import { useThemeColors } from '../../common/ThemeContext'

const { Title, Text, Paragraph, Link } = Typography

const DEFAULT_WEBSITE_URL = 'https://www.edc.com.kh/'

// Read-only brand summary. All editing happens in Organization Profile so
// the org doc and configs/app-settings stay in sync from a single mutation
// path. Subscribes to useBranding() so uploads from anywhere (including the
// Org Profile modal) reflect here without a manual refresh.
const BrandingTab: React.FC = () => {
	const { primary } = useThemeColors()
	const navigate = useNavigate()
	const branding = useBranding()
	const [initialized, setInitialized] = useState<boolean>(
		branding.brandingLogoUrl != null ||
			branding.organizationDisplayName !== 'POWER PLANT C7' ||
			branding.organizationWebsiteUrl != null,
	)

	useEffect(() => {
		if (!initialized) {
			setInitialized(true)
		}
	}, [branding, initialized])

	const editButton = (
		<Button
			type='primary'
			icon={<EditOutlined />}
			onClick={() => navigate('/organization')}
			style={{ borderRadius: 6 }}
		>
			Edit in Organization Profile
		</Button>
	)

	const CARD_STYLE = {
		borderRadius: 12,
		boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
	}

	return (
		<Spin spinning={!initialized}>
			<div style={{ maxWidth: 1000, margin: '0 auto' }}>
				<Card style={{ ...CARD_STYLE, marginBottom: 24 }}>
					<Paragraph type='secondary' style={{ marginBottom: 0 }}>
						Read-only overview of the organization's brand assets. To change the logo, display name, or website, please visit the <Text strong>Organization Profile</Text>.
					</Paragraph>
				</Card>

				<Row gutter={[24, 24]}>
					<Col xs={24} md={10}>
						<Card
							title="Logo Preview"
							style={CARD_STYLE}
							styles={{ body: { padding: 24, textAlign: 'center' } }}
						>
							<div
								style={{
									width: '100%',
									aspectRatio: '1 / 1',
									maxWidth: 240,
									margin: '0 auto',
									borderRadius: 16,
									backgroundColor: '#f8fafc',
									border: '1px dashed #e2e8f0',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									overflow: 'hidden',
								}}
							>
								{branding.brandingLogoUrl ? (
									<img
										src={branding.brandingLogoUrl}
										alt='Branding logo'
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'contain',
											padding: 16,
										}}
									/>
								) : (
									<div style={{ textAlign: 'center', color: '#94a3b8' }}>
										<PictureOutlined style={{ fontSize: 48 }} />
										<div style={{ marginTop: 8, fontSize: 13 }}>No logo uploaded</div>
									</div>
								)}
							</div>
						</Card>
					</Col>

					<Col xs={24} md={14}>
						<Card
							title="Organization Identity"
							style={CARD_STYLE}
							extra={editButton}
						>
							<Space direction='vertical' size={16} style={{ width: '100%' }}>
								<div
									style={{
										backgroundColor: '#f8fafc',
										border: '1px solid #f1f5f9',
										borderRadius: 10,
										padding: '16px',
										display: 'flex',
										alignItems: 'center',
										gap: 16,
									}}
								>
									<div style={{ background: `${primary}10`, padding: 8, borderRadius: 8 }}>
										<BankOutlined style={{ color: primary, fontSize: 18 }} />
									</div>
									<div>
										<Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Display Name</Text>
										<div style={{ fontSize: 16, fontWeight: 600 }}>{branding.organizationDisplayName}</div>
									</div>
								</div>

								<div
									style={{
										backgroundColor: '#f8fafc',
										border: '1px solid #f1f5f9',
										borderRadius: 10,
										padding: '16px',
										display: 'flex',
										alignItems: 'center',
										gap: 16,
									}}
								>
									<div style={{ background: `${primary}10`, padding: 8, borderRadius: 8 }}>
										<GlobalOutlined style={{ color: primary, fontSize: 18 }} />
									</div>
									<div style={{ flex: 1, minWidth: 0 }}>
										<Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Website</Text>
										<div style={{ fontSize: 15 }}>
											{branding.organizationWebsiteUrl ? (
												<Link href={branding.organizationWebsiteUrl} target='_blank' rel='noreferrer' style={{ wordBreak: 'break-all' }}>
													{branding.organizationWebsiteUrl}
												</Link>
											) : (
												<Text type='secondary' italic>Not set</Text>
											)}
										</div>
									</div>
								</div>

								<div
									style={{
										backgroundColor: '#f8fafc',
										border: '1px solid #f1f5f9',
										borderRadius: 10,
										padding: '16px',
										display: 'flex',
										alignItems: 'center',
										gap: 16,
									}}
								>
									<div style={{ background: `${primary}10`, padding: 8, borderRadius: 8 }}>
										<PictureOutlined style={{ color: primary, fontSize: 18 }} />
									</div>
									<div>
										<Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Status</Text>
										<div>
											<Tag color={branding.brandingLogoUrl ? 'success' : 'default'} style={{ borderRadius: 4 }}>
												{branding.brandingLogoUrl ? 'Custom Logo Active' : 'Default Theme Active'}
											</Tag>
										</div>
									</div>
								</div>
							</Space>
						</Card>
					</Col>
				</Row>
			</div>
		</Spin>
	)
}

export default BrandingTab
